import { useCallback } from 'react';
import { type TwoFactorActionOptions } from './types';
import { isMfaError } from './resolveMfaResponse';
import { useMfaIdentitiesModal } from './useMfaIdentitiesModal';
import { useSessionStore } from '@/entities/session';
import { MFA_REQUIRED_RESPONSE_CODE } from '@/shared/config';

export function useTwoFactorAction<
  TResponse = void,
  TBaseArgs extends object = Record<string, unknown>,
>({
  requestFn,
  onBack,
  onSuccess,
  onError,
}: TwoFactorActionOptions<TResponse, TBaseArgs>) {
  const { openMfaConfirmationModal } = useMfaIdentitiesModal();
  const { logout } = useSessionStore();

  const execute = useCallback(
    async (baseArgs: TBaseArgs = {} as TBaseArgs) => {
      try {
        const response = await requestFn({ ...baseArgs, twoFA: null });

        if (!isMfaError(response)) {
          onSuccess(response);
          return;
        }

        if (response.code === MFA_REQUIRED_RESPONSE_CODE && response.data) {
          openMfaConfirmationModal({
            onBack,
            requestFn: async twoFA => await requestFn({ ...baseArgs, twoFA }),
            mfaData: response.data,
            onSuccess: res => onSuccess(res as TResponse),
          });
          return;
        }

        if (response.code === 'NEED_PASSWORD_LOGIN') {
          logout();
          return;
        }

        onError(response);
      } catch (e) {
        onError(e instanceof Error ? e.message : 'Unknown error');
      }
    },
    [requestFn, onSuccess, onBack, openMfaConfirmationModal, logout, onError]
  );

  return {
    execute,
  };
}
